import { createContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { getDefaultLabel } from './defaultLabels';
import { getPtPtFallbackLabel } from './ptPtDefaults';

export type SupportedLang = 'en' | 'pt-pt';

interface I18nState {
  lang: SupportedLang;
  setLang: (lang: SupportedLang) => void;
  t: (key: string, placeholders?: Record<string, string | number>) => string;
  ready: boolean;
  enabled: boolean;
}

export const I18nContext = createContext<I18nState>({
  lang: 'en',
  setLang: () => {},
  t: (key: string) => getDefaultLabel(key),
  ready: false,
  enabled: true,
});

const STORAGE_PREFIX = 'karaokr-i18n';
const SUPPORTED_LANGS: SupportedLang[] = ['en', 'pt-pt'];
const DEFAULT_LANG: SupportedLang = 'en';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function getInitialLang(): SupportedLang {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const stored = localStorage.getItem(`${STORAGE_PREFIX}-lang`) as SupportedLang | null;
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  return DEFAULT_LANG;
}

function getCachedLabels(lang: SupportedLang): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const tsStr = localStorage.getItem(`${STORAGE_PREFIX}-${lang}-ts`);
  if (!tsStr) return null;
  const ts = parseInt(tsStr, 10);
  if (Number.isNaN(ts) || Date.now() - ts > CACHE_TTL_MS) return null;
  const raw = localStorage.getItem(`${STORAGE_PREFIX}-${lang}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return null;
  }
}

function setCachedLabels(lang: SupportedLang, labels: Record<string, string>) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(`${STORAGE_PREFIX}-${lang}`, JSON.stringify(labels));
  localStorage.setItem(`${STORAGE_PREFIX}-${lang}-ts`, Date.now().toString());
}

const I18N_BASE_URL =
  (import.meta.env.VITE_FESTIVAL_SERVICE_URL as string | undefined) || '';

async function fetchLabels(lang: SupportedLang): Promise<Record<string, string>> {
  const base = I18N_BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${base}/api/i18n/${lang}.json`);
  if (!res.ok) {
    throw new Error(`Failed to load translations: ${res.status}`);
  }
  return res.json();
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const enabled = true;
  const [lang, setLangState] = useState<SupportedLang>(() => getInitialLang());
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [ready, setReady] = useState(false);

  const setLang = useCallback((next: SupportedLang) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${STORAGE_PREFIX}-lang`, next);
    }
    setLangState(next);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setReady(false);
      try {
        const cached = getCachedLabels(lang);
        if (cached) {
          setLabels(cached);
          setReady(true);
          // Refresh in background after returning cached value.
          fetchLabels(lang)
            .then((fresh) => {
              if (cancelled) return;
              setCachedLabels(lang, fresh);
              setLabels(fresh);
            })
            .catch(() => {});
          return;
        }

        const fresh = await fetchLabels(lang);
        if (!cancelled) {
          setCachedLabels(lang, fresh);
          setLabels(fresh);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          setLabels({});
          setReady(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [lang, enabled]);

  const t = useCallback(
    (key: string, placeholders?: Record<string, string | number>): string => {
      // Prioridade: labels remotos → fallback local pt-PT (só em pt-PT) →
      // defaults em inglês. Assim o toggle de idioma funciona mesmo sem o
      // backend de traduções.
      let value =
        labels[key] ||
        (lang === 'pt-pt' ? getPtPtFallbackLabel(key) : undefined) ||
        getDefaultLabel(key);
      if (placeholders) {
        Object.entries(placeholders).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return value;
    },
    [labels, lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, ready, enabled }),
    [lang, setLang, t, ready, enabled]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
